import type { SectionView, StepView } from '~/helpers/learner/session-view'
import { Clock, PlayCircle, Target } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { formatMinutes, formatSeconds } from '~/helpers/learner/duration'
import { STEP_ACTION_KEYS, stepAction, stepScore, stepThreshold } from '~/helpers/learner/session-view'
import LockNotice from '~/pages/scorm/components/LockNotice'
import ScoreValue from '~/pages/scorm/components/ScoreValue'

type IProps = {
  /** L'étape mise en avant : celle à reprendre, ou la première fermée. */
  step: StepView
  /** Toutes les étapes du parcours : la raison d'un verrou peut nommer un autre module. */
  steps: StepView[]
  total: number
  sections: readonly SectionView[]
  onLaunch: (moduleId: string) => void
}

/**
 * Le panneau de l'étape mise en avant sur l'écran d'un parcours (#74) : son
 * chapitre et son rang (#75), sa durée, son score, et de quoi la lancer — ou
 * pourquoi elle est fermée.
 */
function PathFocus({ step, steps, total, sections, onLaunch }: IProps) {
  const { t } = useTranslation()
  // Durée estimée et temps passé restent deux libellés distincts (#90).
  const estimated = formatMinutes(step.module?.durationMinutes, t)
  const spent = formatSeconds(step.myTracking?.totalTimeSeconds, t)
  const threshold = stepThreshold(step)
  const score = stepScore(step)
  const action = stepAction(step)
  // Le chapitre de l'étape (#75) : il situe l'apprenant dans un long parcours.
  const section = sections.find(candidate => candidate.id === step.sectionId)

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {section && (
          <>
            {t('path.chapter', { number: section.position + 1, title: section.title })}
            {' · '}
          </>
        )}
        {t('path.stepOf', { position: step.position + 1, total })}
      </p>
      <h2 className="font-heading text-xl leading-tight text-balance">
        {step.module?.title}
      </h2>

      {step.module?.description && (
        <p className="text-sm text-muted-foreground">{step.module.description}</p>
      )}

      {(estimated || spent || threshold != null) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {estimated && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-3.5" />
              {t('home.meta.estimated', { duration: estimated })}
            </span>
          )}
          {spent && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-3.5" />
              {t('home.meta.spent', { duration: spent })}
            </span>
          )}
          {threshold != null && (
            <span className="inline-flex items-center gap-1.5">
              <Target className="size-3.5" />
              {t('home.meta.threshold', { threshold })}
            </span>
          )}
        </div>
      )}

      {score && (
        <p className="text-sm text-muted-foreground">
          {t('common.score')}
          {' : '}
          <ScoreValue score={score} />
        </p>
      )}

      {step.unlocked === false
        ? (
            <LockNotice step={step} steps={steps} />
          )
        : step.module?.id && action && (
          <Button
            size="lg"
            className="w-full sm:w-fit"
            disabled={step.module.status !== 'READY'}
            onClick={() => onLaunch(step.module!.id)}
          >
            <PlayCircle className="mr-2 size-4" />
            {t(STEP_ACTION_KEYS[action])}
          </Button>
        )}
    </>
  )
}

export default PathFocus
